import { move } from '@dnd-kit/helpers'
import { DragDropProvider, type DragEndEvent } from '@dnd-kit/react'
import { useSortable } from '@dnd-kit/react/sortable'
import { useState } from 'react'

import { FvButton } from '@fv/client-components'
import type { ColumnPreference } from '@fv/models'

import CheckboxField from '@/components/inputs/CheckboxField.js'
import { List, ListItem } from '@/components/List.js'

import { useUpdateColumnPreferences } from '../settings/user-preferences/mutations.js'
import { columnsRegistry } from './Cells.js'
import { useColumnPreferences } from './hooks.js'

type Props = {
  closePanel: () => void
}

export const ColumnEditSlider = ({ closePanel }: Props) => {
  const columnPrefs = useColumnPreferences()
  const updatePrefs = useUpdateColumnPreferences()
  const [columns, setColumns] = useState<ColumnPreference[]>(columnPrefs)

  const handleColumnToggle = (columnId: string, checked: boolean) => {
    setColumns(prev =>
      prev.map(col =>
        col.id === columnId ? { ...col, visible: checked } : col,
      ),
    )
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setColumns(cols => move(cols, event))
  }

  const handleSubmit = async () => {
    await updatePrefs.mutateAsync(columns)
    closePanel()
  }

  return (
    <div className="flex flex-col h-full -mx-6">
      <div className="flex-1 overflow-auto pb-4 px-6">
        <p className="mb-3">
          Choose which columns to display in the shipment table.
        </p>

        <DragDropProvider onDragEnd={handleDragEnd}>
          <List>
            {columns.map((column, index) => (
              <ColumnItem
                key={column.id}
                column={column}
                index={index}
                onColumnToggle={handleColumnToggle}
              />
            ))}
          </List>
        </DragDropProvider>
      </div>
      <div className="bg-fv-blue-50 mt-auto px-6 py-4 border-t border-fv-blue-200 -my-6">
        <div className="flex gap-x-3 items-center">
          <FvButton
            loading={updatePrefs.isPending}
            onClick={handleSubmit}
            theme="primary"
          >
            Apply Changes
          </FvButton>

          <FvButton
            loading={updatePrefs.isPending}
            onClick={closePanel}
            icon="times"
          >
            Cancel
          </FvButton>
        </div>
      </div>
    </div>
  )
}

type ColumnItemProps = {
  column: ColumnPreference
  index: number
  onColumnToggle: (columnId: string, checked: boolean) => void
}
const ColumnItem = ({ column, index, onColumnToggle }: ColumnItemProps) => {
  const [element, setElement] = useState<Element | null>(null)
  useSortable({ id: column.id, index, element })
  return (
    <ListItem key={column.id} className="cursor-move" ref={setElement}>
      <CheckboxField
        name={column.id}
        checked={column.visible}
        stopPropagation /* stopPropagation is needed to prevent interference with the drag and drop */
        onChange={e => onColumnToggle(column.id, e.target.checked)}
        label={columnsRegistry[column.id]?.label ?? column.id}
      />

      <FvButton
        icon="grip-dots"
        transform="grow-4"
        className="mr-2 ml-auto cursor-move pointer-events-none"
      />
    </ListItem>
  )
}
